import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '@/api/useAccount';
import { formatInstantWithZone } from '@/lib/format';
import { AppointmentRefusedError, type AppointmentEmailsChange } from '@/routes/appointments/useAppointmentEmails';

/**
 * One recipient's "Appointment emails" switch (M2-21, Kyle's 8): on the
 * customer's page, and on the property's occupant/tenant link. It covers
 * confirmations, reminders, updates and "on my way"; invoices and estimates
 * ignore it, and the hint says so.
 *
 * `customers.manage` flips it (the API's gate on both routes); everyone else
 * reads a sentence. Each press saves at once, like the invoice's reminder
 * switch (M2-16), and is held while the save runs. A refusal is the API's own
 * sentence.
 *
 * Under it, who last changed it and when — the recipient from their own email
 * link, or a named office user — because "why did Pat stop getting
 * reminders?" is the question this switch gets asked about. The time is on the
 * ACCOUNT's clock with the zone named, as the settings trail is; until the
 * account is loaded it is left out rather than shown on the viewer's.
 */
export function AppointmentEmailsSwitch({
  enabled,
  lastChange,
  canManage,
  recipient,
  name,
  isPending,
  onToggle,
  testId,
}: {
  enabled: boolean;
  lastChange: AppointmentEmailsChange | null | undefined;
  canManage: boolean;
  /** Which recipient this is, for the sentences: the customer, or the occupant. */
  recipient: 'customer' | 'occupant';
  /** The person's name, for the announcement. */
  name: string;
  isPending: boolean;
  onToggle: (enabled: boolean) => Promise<unknown>;
  testId?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const timeZone = useAccount().data?.timezone;
  const [problem, setProblem] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  async function toggle(next: boolean) {
    setProblem(null);
    setAnnouncement('');
    try {
      await onToggle(next);
      setAnnouncement(
        next ? t('appointmentEmails.switch.turnedOn', { name }) : t('appointmentEmails.switch.turnedOff', { name }),
      );
    } catch (error) {
      setProblem(
        error instanceof AppointmentRefusedError
          ? t('appointmentEmails.refused', { reason: error.message })
          : t('appointmentEmails.switch.failed'),
      );
    }
  }

  const changed = lastChange ? describeChange(lastChange, recipient, timeZone, t) : null;

  return (
    <div className="space-y-1 text-sm" data-testid={testId}>
      {canManage ? (
        <div>
          <label className="flex items-center gap-2 font-medium" htmlFor={`${id}-toggle`}>
            <input
              id={`${id}-toggle`}
              type="checkbox"
              checked={enabled}
              disabled={isPending}
              aria-describedby={`${id}-hint`}
              onChange={(event) => void toggle(event.target.checked)}
            />
            {t('appointmentEmails.switch.label')}
          </label>
          <p id={`${id}-hint`} className="ml-6 text-[var(--color-muted-foreground)]">
            {t('appointmentEmails.switch.hint')}
          </p>
        </div>
      ) : (
        <p data-testid="appointment-emails-state">
          {enabled ? t(`appointmentEmails.switch.readOn.${recipient}`) : t(`appointmentEmails.switch.readOff.${recipient}`)}
        </p>
      )}
      {changed ? (
        <p className="text-[var(--color-muted-foreground)]" data-testid="appointment-emails-changed">
          {changed}
        </p>
      ) : null}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {problem ? (
        <p role="alert" className="text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

function describeChange(
  change: AppointmentEmailsChange,
  recipient: 'customer' | 'occupant',
  timeZone: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const state = change.enabled ? 'on' : 'off';
  const who = change.byRecipient
    ? t(`appointmentEmails.switch.byRecipient.${recipient}`)
    : (change.byUserName ?? t('appointmentEmails.switch.byOffice'));
  return timeZone
    ? t(`appointmentEmails.switch.changed.${state}`, { who, when: formatInstantWithZone(change.changedAt, timeZone) })
    : t(`appointmentEmails.switch.changedNoTime.${state}`, { who });
}
